import {
  GalaxyKPIDrivers,
  KPIUsageSummary,
  KPIVariant,
  RootKPI,
} from '@services/api/deckManagement/graphql/schema/API';
import { ReactNode } from 'react';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { KpiDimension, KpiDriverValues } from 'types/PageSidePanel.types';

/**
 * Result returned when a KPI is assigned from the library.
 */
export interface KPILibrarySelection {
  rootKPI: RootKPI;
  variant: KPIVariant;
}

/**
 * Display mode for the KPI Library component
 */
export enum KPILibraryMode {
  DIALOG = 'dialog',
  PAGE = 'page',
}

/**
 * View options for the KPI Library dialog
 */
export enum KPILibraryView {
  ROOT_KPI_VIEW = 'rootKPIView',
  VARIANT_VIEW = 'variantView',
  EDIT_VARIANT_VIEW = 'editVariantView',
}

/**
 * Imperative handle exposed via ref for programmatic control of the KPI Library modal.
 */
export interface KPILibraryHandle {
  /** Opens the modal, optionally into a specific view. Returns a promise that resolves with the selection, or null if closed without selecting. */
  open: (view?: KPILibraryView) => Promise<KPILibrarySelection | null>;
  /** Closes the modal and resolves any pending promise with null. */
  close: () => void;
}

/**
 * Interface Props for the KPILibrary component
 * Supports both declarative (prop-driven) and imperative (ref-driven) usage.
 *
 * @param isLibraryOpen - Controlled open state (declarative path)
 * @param setIsLibraryOpen - Controlled close handler (declarative path)
 * @param rootKPI - Optional Root KPI to pre-select (opens into variant or edit variant view)
 * @param variant - Optional KPI Variant to pre-select (opens into edit variant view)
 * @param onSelect - Callback when a variant is selected and assigned (declarative path)
 */
export interface KPILibraryProps {
  /** Display mode — dialog (modal) or page (full page). Defaults to dialog. */
  mode?: KPILibraryMode;
  isLibraryOpen?: boolean;
  setIsLibraryOpen?: (isOpen: boolean) => void;
  rootKPI?: RootKPI | null;
  variant?: KPIVariant | null;
  /** Page drivers used as placeholder values for inactive dimensions in Edit Variant view */
  pageDrivers?: GalaxyKPIDrivers | null;
  onSelect?: (selection: KPILibrarySelection) => void;
}

/**
 * Props for the EditVariantView component.
 */
export interface EditVariantViewProps {
  selectedRootKPI: RootKPI;
  selectedVariant: KPIVariant;
  editVariantName: string;
  onVariantNameChange: (name: string) => void;
  driverDisplayOptions: DriverDisplayOptionMap;
  placeholderDrivers?: GalaxyKPIDrivers | null;
  onDriverChange: (key: KpiDimension, updates: KpiDriverValues) => void;
  onDriverToggle: (key: KpiDimension, checked: boolean) => void;
  usageSummary: KPIUsageSummary | null | undefined;
  isUsageSummaryLoading: boolean;
  expandedDeckId: string | null;
  onExpandChange: (deckId: string | null) => void;
  isDialogMode: boolean;
  editVariantActions: ReactNode;
}

/**
 * Props for the ConfirmActionView component used in confirm-save and confirm-delete flows.
 */
export interface ConfirmActionViewProps {
  /** CSS class for the outer content container */
  wrapperClassName: string;
  /** CSS class for the message container */
  msgClassName: string;
  /** Paragraphs to display as the confirmation message */
  messages: string[];
  /** Usage summary data */
  usageSummary: KPIUsageSummary | null | undefined;
  isUsageSummaryLoading: boolean;
  /** For tracking usage summary accordion expanded state from parent */
  expandedDeckId: string | null;
  onExpandChange: (deckId: string | null) => void;
  /** Whether to render actions in DialogActions (dialog mode) */
  isDialogMode: boolean;
  /** Action buttons to render */
  confirmActions: ReactNode;
}
